import { createSlice } from '@reduxjs/toolkit';
import { apiSlice } from './apiSlice.js';
import { loggedOut } from './authActions.js';

/**
 * Who is signed in.
 *
 * No credential is ever stored here — the tokens live in httpOnly cookies the
 * page cannot read. This slice only mirrors the user record the server returns,
 * so a component can ask "what may I show?" without waiting on a request.
 *
 * `booted` is false until the first `/auth/me` settles, one way or the other.
 * Route guards must not bounce anyone to /login before that answer arrives.
 */
const initialState = { user: null, booted: false };

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loggedOut, (state) => {
        state.user = null;
        state.booted = true;
      })
      // The boot check. A 401 here is not an error — it means nobody is signed in.
      .addMatcher(apiSlice.endpoints.getMe.matchFulfilled, (state, { payload }) => {
        state.user = payload;
        state.booted = true;
      })
      .addMatcher(apiSlice.endpoints.getMe.matchRejected, (state) => {
        state.user = null;
        state.booted = true;
      })
      .addMatcher(apiSlice.endpoints.login.matchFulfilled, (state, { payload }) => {
        state.user = payload;
        state.booted = true;
      })
      // Registering the *first* account signs you in; later ones return no user.
      .addMatcher(apiSlice.endpoints.register.matchFulfilled, (state, { payload }) => {
        if (payload.user) {
          state.user = payload.user;
          state.booted = true;
        }
      });
  },
});

export default authSlice.reducer;

export const selectUser = (state) => state.auth.user;
export const selectBooted = (state) => state.auth.booted;
